"use client";

import Icon from "@/components/ui/AppIcon";
import type { PageTexts } from "@/lib/siteTexts";

interface DocArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  is_published: boolean;
  views_count: number;
}

interface DocsContentProps {
  article: DocArticle | null;
  loading?: boolean;
  texts: PageTexts<"documentation">;
}

export default function DocsContent({ article, loading, texts: t }: DocsContentProps) {
  if (loading) {
    return (
      <div className="flex-1 glass rounded-2xl p-8 lg:p-10 border border-[rgba(79,124,255,0.12)] min-h-[600px] flex items-center justify-center">
        <div className="text-[#4A6080] text-sm">{t["article.loading"]}</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="flex-1 glass rounded-2xl p-10 border border-[rgba(79,124,255,0.12)] flex flex-col items-center justify-center text-center min-h-[400px]">
        <div className="w-16 h-16 rounded-2xl bg-[rgba(79,124,255,0.1)] flex items-center justify-center mb-5">
          <Icon name="DocumentTextIcon" size={32} className="text-[#4F7CFF]" />
        </div>
        <h3 className="text-white font-bold text-xl mb-2">{t["article.empty.title"]}</h3>
        <p className="text-[#4A6080] text-sm max-w-sm">
          {t["article.empty.text"]}
        </p>
      </div>
    );
  }

  const renderInline = (text: string): React.ReactNode => {
    const parts = text.split(/\*\*(.+?)\*\*/);
    if (parts.length === 1) return text;
    return parts.map((part, idx) =>
      idx % 2 === 1 ? <strong key={idx} className="text-white font-semibold">{part}</strong> : part
    );
  };

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('```')) {
        const lang = line.slice(3).trim();
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        elements.push(
          <div key={`code-${i}`} className="terminal rounded-xl overflow-hidden my-4">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[rgba(0,212,255,0.1)] bg-[rgba(0,0,0,0.3)]">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </div>
              {lang && <span className="text-xs text-[#4A6080] ml-2">{lang}</span>}
            </div>
            <pre className="p-5 text-xs text-[#8BA3CC] overflow-x-auto leading-relaxed">
              <code>{codeLines.join('\n')}</code>
            </pre>
          </div>
        );
        i++;
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(<h1 key={`h1-${i}`} className="font-display font-bold text-3xl text-white mb-3 mt-2">{line.slice(3)}</h1>);
        i++; continue;
      }
      if (line.startsWith('### ')) {
        elements.push(<h2 key={`h2-${i}`} className="font-display font-bold text-xl text-white mb-3 mt-6">{line.slice(4)}</h2>);
        i++; continue;
      }
      if (line.startsWith('#### ')) {
        elements.push(<h3 key={`h3-${i}`} className="font-semibold text-lg text-[#8BA3CC] mb-2 mt-4">{line.slice(5)}</h3>);
        i++; continue;
      }

      // A line of its own: ![caption](/docs-img/file.png) or an https:// address.
      const image = line.trim().match(/^!\[([^\]]*)\]\(((?:\/|https:\/\/)[^\s)]+)\)$/);
      if (image) {
        elements.push(
          <figure key={`img-${i}`} className="my-6">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={image[2]}
              alt={image[1]}
              loading="lazy"
              className="w-full h-auto rounded-xl border border-[rgba(79,124,255,0.2)]"
            />
            {image[1] && <figcaption className="mt-2 text-xs text-[#4A6080] text-center">{image[1]}</figcaption>}
          </figure>
        );
        i++; continue;
      }

      if (line.startsWith('> ')) {
        elements.push(
          <div key={`bq-${i}`} className="glass rounded-2xl p-6 border border-[rgba(0,212,255,0.2)] bg-[rgba(0,212,255,0.03)] my-4">
            <div className="flex items-start gap-3">
              <Icon name="InformationCircleIcon" size={20} className="text-[#00D4FF] flex-shrink-0 mt-0.5" />
              <p className="text-[#8BA3CC] text-sm">{renderInline(line.slice(2))}</p>
            </div>
          </div>
        );
        i++; continue;
      }

      if (line.startsWith('|')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].startsWith('|')) {
          if (!lines[i].match(/^\|[-| ]+\|$/)) tableLines.push(lines[i]);
          i++;
        }
        if (tableLines.length > 0) {
          const headers = tableLines[0].split('|').filter(Boolean).map((h) => h.trim());
          const rows = tableLines.slice(1).map((r) => r.split('|').filter(Boolean).map((c) => c.trim()));
          elements.push(
            <div key={`table-${i}`} className="overflow-x-auto my-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(79,124,255,0.2)]">
                    {headers.map((h, hi) => <th key={hi} className="text-left py-2 px-3 text-[#4F7CFF] font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri} className="border-b border-[rgba(79,124,255,0.08)]">
                      {row.map((cell, ci) => <td key={ci} className="py-2 px-3 text-[#8BA3CC]">{renderInline(cell)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }

      if (line.startsWith('- ')) {
        const listItems: string[] = [];
        while (i < lines.length && lines[i].startsWith('- ')) {
          listItems.push(lines[i].slice(2));
          i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className="space-y-1.5 my-3 ml-4">
            {listItems.map((item, li) => (
              <li key={li} className="flex items-start gap-2 text-[#8BA3CC] text-sm">
                <span className="text-[#4F7CFF] mt-1.5 flex-shrink-0">•</span>
                <span>{renderInline(item)}</span>
              </li>
            ))}
          </ul>
        );
        continue;
      }

      if (/^\d+\.\s/.test(line)) {
        const listItems: string[] = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
          listItems.push(lines[i].replace(/^\d+\.\s/, ''));
          i++;
        }
        elements.push(
          <ol key={`ol-${i}`} className="space-y-1.5 my-3 ml-4 list-decimal list-inside">
            {listItems.map((item, li) => <li key={li} className="text-[#8BA3CC] text-sm">{renderInline(item)}</li>)}
          </ol>
        );
        continue;
      }

      if (line.trim() === '') { i++; continue; }

      elements.push(
        <p key={`p-${i}`} className="text-[#8BA3CC] text-sm leading-relaxed mb-3">
          {renderInline(line)}
        </p>
      );
      i++;
    }
    return elements;
  };

  return (
    <div className="flex-1 glass rounded-2xl p-8 lg:p-10 border border-[rgba(79,124,255,0.12)] min-h-[600px]">
      {article.excerpt && (
        <p className="text-[#8BA3CC] text-lg leading-relaxed mb-6">{article.excerpt}</p>
      )}
      <div>{renderContent(article.content)}</div>
    </div>
  );
}